import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthProvider, useAuth } from './context/AuthContext';
import { OrgContextProvider } from './context/OrgContext';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Discovery from './pages/Discovery';
import Connections from './pages/Connections';
import Groups from './pages/Groups';
import GroupDetails from './pages/GroupDetails';
import Events from './pages/Events';
import EventDetails from './pages/EventDetails';
import Heroes from './pages/Heroes';
import HeroDetails from './pages/HeroDetails';
import AdminHeroes from './pages/AdminHeroes';
import AdminCategoryRequests from './pages/AdminCategoryRequests';
import DebugRequests from './pages/DebugRequests';
import VerifyEmail from './pages/VerifyEmail';
import BlockedUsers from './pages/BlockedUsers';
import MyReports from './pages/MyReports';
import AdminModeration from './pages/AdminModeration';
import AdminContentReview from './pages/AdminContentReview';
import ContentStudio from './pages/ContentStudio';
import AdminModerators from './pages/AdminModerators';
import Notifications from './pages/Notifications';
import Messages from './pages/Messages';
import Chat from './pages/Chat';
import MyCategoryRequests from './pages/MyCategoryRequests';
import ContactUs from './pages/ContactUs';
import MyFeedback from './pages/MyFeedback';
import AdminFeedback from './pages/AdminFeedback';
import AdminReferrals from './pages/AdminReferrals';
import Ambassador from './pages/Ambassador';
import PublicContact from './pages/PublicContact';
import FAQPage from './pages/FAQPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import SmsConsent from './pages/SmsConsent';
import About from './pages/About';
import Landing from './pages/Landing';
import ChannelFeed from './pages/ChannelFeed';
import PublicChannelBrowse from './pages/PublicChannelBrowse';
import Contribute from './pages/Contribute';
import AdminContributorApps from './pages/AdminContributorApps';
import PublicChannelFeed from './pages/PublicChannelFeed';
import OrgSignup from './pages/OrgSignup';
import OrgOnboard from './pages/OrgOnboard';
import OrgLanding from './pages/OrgLanding';
import AdminOrgRequests from './pages/AdminOrgRequests';
import OrgRoute from './components/OrgRoute';
import OrgGuard from './components/OrgGuard';
import OrgAdminDashboard from './pages/OrgAdminDashboard';
import OrgInsightsDashboard from './pages/OrgInsightsDashboard';
import OrgCommunities from './pages/OrgCommunities';
import PrivacyCenter from './pages/PrivacyCenter';
import AnalyticsTracker from './components/AnalyticsTracker';
import './App.css';

// Configure axios baseURL for production
if (process.env.REACT_APP_API_URL) {
  axios.defaults.baseURL = process.env.REACT_APP_API_URL;
  console.log('API URL configured:', process.env.REACT_APP_API_URL);
} else {
  console.log('Using relative API URLs (development mode)');
}

function PrivateRoute({ children }) {
  const { user } = useAuth();
  const location = window.location;
  if (!user) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnTo=${returnTo}`} />;
  }
  return children;
}

function HomeRoute() {
  const { user } = useAuth();
  return user ? <Navigate to="/dashboard" /> : <PublicChannelBrowse />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <OrgContextProvider>
        <Router>
          <ScrollToTop />
          <AnalyticsTracker />
          <div className="App">
            <Routes>
              {/* Public routes — no org guard */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/help" element={<PublicContact />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/sms-consent" element={<SmsConsent />} />
              <Route path="/about" element={<About />} />
              <Route path="/welcome" element={<Landing />} />
              <Route path="/org/signup" element={<OrgSignup />} />
              <Route path="/org/onboard" element={<OrgOnboard />} />
              <Route path="/org/:slug/onboard" element={<OrgOnboard />} />
              <Route path="/b" element={<PublicChannelBrowse />} />
              <Route path="/b/:slug" element={<PublicChannelFeed />} />

              {/* Org landing page (public) */}
              <Route path="/org/:slug" element={<OrgLanding />} />

              {/* Redirect /org/:slug/b to public browse — org channels are never public */}
              <Route path="/org/:slug/b" element={<Navigate to="/b" replace />} />

              {/* Consumer routes — OrgGuard redirects org users to /org/:slug/* */}
              <Route path="/dashboard" element={
                <PrivateRoute>
                  <OrgGuard>
                    <Dashboard />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/profile" element={
                <PrivateRoute>
                  <OrgGuard>
                    <Profile />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/discovery" element={
                <PrivateRoute>
                  <OrgGuard>
                    <Discovery />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/connections" element={
                <PrivateRoute>
                  <OrgGuard>
                    <Connections />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/groups" element={
                <PrivateRoute>
                  <OrgGuard>
                    <Groups />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/groups/:groupId" element={
                <PrivateRoute>
                  <OrgGuard>
                    <GroupDetails />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/events" element={
                <PrivateRoute>
                  <OrgGuard>
                    <Events />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/events/:eventId" element={
                <PrivateRoute>
                  <OrgGuard>
                    <EventDetails />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/community" element={<Navigate to="/dashboard" />} />
              <Route path="/community/:postId" element={<Navigate to="/dashboard" />} />
              <Route path="/channels/:channelId" element={
                <PrivateRoute>
                  <OrgGuard>
                    <ChannelFeed />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/heroes" element={
                <PrivateRoute>
                  <OrgGuard>
                    <Heroes />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/heroes/:personId" element={
                <PrivateRoute>
                  <OrgGuard>
                    <HeroDetails />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/admin/heroes" element={
                <PrivateRoute>
                  <AdminHeroes />
                </PrivateRoute>
              } />
              <Route path="/admin/category-requests" element={
                <PrivateRoute>
                  <AdminCategoryRequests />
                </PrivateRoute>
              } />
              <Route path="/debug" element={
                <PrivateRoute>
                  <DebugRequests />
                </PrivateRoute>
              } />
              <Route path="/blocked-users" element={
                <PrivateRoute>
                  <OrgGuard>
                    <BlockedUsers />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/my-reports" element={
                <PrivateRoute>
                  <OrgGuard>
                    <MyReports />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/admin/moderation" element={
                <PrivateRoute>
                  <AdminModeration />
                </PrivateRoute>
              } />
              <Route path="/admin/content-review" element={
                <PrivateRoute>
                  <AdminContentReview />
                </PrivateRoute>
              } />
              <Route path="/content-studio" element={
                <PrivateRoute>
                  <ContentStudio />
                </PrivateRoute>
              } />
              <Route path="/contribute" element={<Contribute />} />
              <Route path="/admin/contributor-applications" element={
                <PrivateRoute>
                  <AdminContributorApps />
                </PrivateRoute>
              } />
              <Route path="/admin/moderators" element={
                <PrivateRoute>
                  <AdminModerators />
                </PrivateRoute>
              } />
              <Route path="/admin/org-requests" element={
                <PrivateRoute>
                  <AdminOrgRequests />
                </PrivateRoute>
              } />
              <Route path="/notifications" element={
                <PrivateRoute>
                  <OrgGuard>
                    <Notifications />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/messages" element={
                <PrivateRoute>
                  <OrgGuard>
                    <Messages />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/chat/:userId" element={
                <PrivateRoute>
                  <OrgGuard>
                    <Chat />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/category-requests" element={
                <PrivateRoute>
                  <OrgGuard>
                    <MyCategoryRequests />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/contact-us" element={
                <PrivateRoute>
                  <OrgGuard>
                    <ContactUs />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/my-feedback" element={
                <PrivateRoute>
                  <OrgGuard>
                    <MyFeedback />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/admin/feedback" element={
                <PrivateRoute>
                  <AdminFeedback />
                </PrivateRoute>
              } />
              <Route path="/admin/referrals" element={
                <PrivateRoute>
                  <AdminReferrals />
                </PrivateRoute>
              } />
              <Route path="/ambassador" element={
                <PrivateRoute>
                  <Ambassador />
                </PrivateRoute>
              } />

              {/* Org-scoped routes — all pages under /org/:slug/ */}
              <Route path="/org/:slug/admin/insights" element={
                <OrgRoute requireAdmin={true}>
                  <OrgInsightsDashboard />
                </OrgRoute>
              } />
              <Route path="/org/:slug/admin" element={
                <OrgRoute requireAdmin={true}>
                  <OrgAdminDashboard />
                </OrgRoute>
              } />
              <Route path="/org/:slug/privacy" element={
                <OrgRoute>
                  <PrivacyCenter />
                </OrgRoute>
              } />
              <Route path="/org/:slug/dashboard" element={
                <OrgRoute>
                  <Dashboard />
                </OrgRoute>
              } />
              <Route path="/org/:slug/communities" element={
                <OrgRoute>
                  <OrgCommunities />
                </OrgRoute>
              } />
              <Route path="/org/:slug/channels/:channelId" element={
                <OrgRoute>
                  <ChannelFeed />
                </OrgRoute>
              } />
              <Route path="/org/:slug/groups" element={
                <OrgRoute>
                  <Groups />
                </OrgRoute>
              } />
              <Route path="/org/:slug/groups/:groupId" element={
                <OrgRoute>
                  <GroupDetails />
                </OrgRoute>
              } />
              <Route path="/org/:slug/events" element={
                <OrgRoute>
                  <Events />
                </OrgRoute>
              } />
              <Route path="/org/:slug/events/:eventId" element={
                <OrgRoute>
                  <EventDetails />
                </OrgRoute>
              } />
              <Route path="/org/:slug/messages" element={
                <OrgRoute>
                  <Messages />
                </OrgRoute>
              } />
              <Route path="/org/:slug/chat/:userId" element={
                <OrgRoute>
                  <Chat />
                </OrgRoute>
              } />
              <Route path="/org/:slug/profile" element={
                <OrgRoute>
                  <Profile />
                </OrgRoute>
              } />
              <Route path="/org/:slug/discovery" element={
                <OrgRoute>
                  <Discovery />
                </OrgRoute>
              } />
              <Route path="/org/:slug/connections" element={
                <OrgRoute>
                  <Connections />
                </OrgRoute>
              } />
              <Route path="/org/:slug/heroes" element={
                <OrgRoute>
                  <Heroes />
                </OrgRoute>
              } />
              <Route path="/org/:slug/heroes/:personId" element={
                <OrgRoute>
                  <HeroDetails />
                </OrgRoute>
              } />
              <Route path="/org/:slug/notifications" element={
                <OrgRoute>
                  <Notifications />
                </OrgRoute>
              } />
              <Route path="/org/:slug/blocked-users" element={
                <OrgRoute>
                  <BlockedUsers />
                </OrgRoute>
              } />
              <Route path="/org/:slug/my-reports" element={
                <OrgRoute>
                  <MyReports />
                </OrgRoute>
              } />
              <Route path="/org/:slug/category-requests" element={
                <OrgRoute>
                  <MyCategoryRequests />
                </OrgRoute>
              } />
              <Route path="/org/:slug/contact-us" element={
                <OrgRoute>
                  <ContactUs />
                </OrgRoute>
              } />
              <Route path="/org/:slug/my-feedback" element={
                <OrgRoute>
                  <MyFeedback />
                </OrgRoute>
              } />

              {/* Slug-less org routes — redirect org users to slug version */}
              <Route path="/org/admin/insights" element={
                <PrivateRoute>
                  <OrgGuard fallbackComponent={<OrgInsightsDashboard />}>
                    <OrgInsightsDashboard />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/org/admin" element={
                <PrivateRoute>
                  <OrgGuard fallbackComponent={<OrgAdminDashboard />}>
                    <OrgAdminDashboard />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/org/admin/*" element={
                <PrivateRoute>
                  <OrgGuard fallbackComponent={<OrgAdminDashboard />}>
                    <OrgAdminDashboard />
                  </OrgGuard>
                </PrivateRoute>
              } />
              <Route path="/org/privacy" element={
                <PrivateRoute>
                  <OrgGuard fallbackComponent={<PrivacyCenter />}>
                    <PrivacyCenter />
                  </OrgGuard>
                </PrivateRoute>
              } />

              <Route path="/" element={<HomeRoute />} />
            </Routes>
          </div>
        </Router>
      </OrgContextProvider>
    </AuthProvider>
  );
}

export default App;
